/* The stack curve and the book's proportions.

   The books ride a conveyor. Seen from the FRONT the column is dead straight
   and every book is square on to the viewport -- no sideways bow, no yaw. The
   J is in the SIDE view: the belt runs flat along the bottom, turns one
   corner, and then goes straight up the screen.

   So the angle is not a linear ramp. It is ~0 for the whole straight run --
   those books are pure spine -- and opens up only through the corner at the
   bottom, where a book is still lying flat enough to show its top board. That
   is what CORNER does: a power curve, flat for most of its range and steep at
   the end.

   p is a book's continuous position down the column; it rises as you go down,
   and scrolling slides every book along it. */
/* Measured off the reference at the same viewport rather than guessed: on a
   950px bar its spine is 110-140px and its top board only 47-75px, so the
   board is well under half the spine even on the most-turned book. That works
   out at 4-7deg. Anything near the 17deg this started at is a wedge. */
const PHI_MAX = 7;
/* A floor, so even the book at the head of the run shows a little of its top
   board. At nought it is a pure spine -- a flat bar with no cover to see. */
const PHI_MIN = 1.6;
const CORNER = 2.6;
/* Air between slabs. The pitch itself is not a constant: a book turned further
   through the corner is a much taller slab (27px square-on, 200px+ at full
   angle), so spacing is integrated from the slab height rather than fixed. */
const SLAB_GAP = 58;
/* The lower a book sits the further back it stands, very slightly. Real depth
   rather than a scale fudge, so the shared camera foreshortens it properly. */
const Z_PER_STEP = -26;
/* Books on screen at once. The rest are parked and scrolled in. */
const SPAN = 5;
/* Pixels of wheel travel per book. */
export const WHEEL_PER_BOOK = 260;

const clamp01 = v => (v < 0 ? 0 : v > 1 ? 1 : v);

/* Flat until the corner, then it turns. Degrees. */
export const phiAt = p =>
  PHI_MIN + (PHI_MAX - PHI_MIN) * Math.pow(clamp01(p / SPAN), CORNER);

/* How tall a book's slab is on screen at that angle -- the same expression the
   stylesheet uses for --barH, so the two cannot disagree. */
function slabAt(p, w, depth) {
  const phi = (phiAt(p) * Math.PI) / 180;
  return depth * Math.cos(phi) + w * Math.sin(phi);
}

/* Distance down the belt to position p, integrated so that consecutive books
   clear each other however far through the corner they are. A fixed pitch
   cannot do this: the slab is an order of magnitude taller at the bottom. */
function yAt(p, w, depth) {
  /* Above the top slot the belt is straight, so carry on at the square-on
     pitch. Without this the integration below simply does not run for p < 0
     and every book that has scrolled off the top piles up on the same spot. */
  if (p <= 0) return p * (slabAt(0, w, depth) + SLAB_GAP);
  const step = 0.05;
  let y = 0;
  for (let t = 0; t < p; t += step) {
    y += (slabAt(t, w, depth) + SLAB_GAP) * Math.min(step, p - t);
  }
  return y;
}

/* Where a book sits, given its position on the belt. Note there is no opacity
   term: books leaving the top are clipped by the page edge, the way the
   reference does it. Fading them would also flatten the 3D, since opacity on
   an ancestor destroys preserve-3d. */
/* A book's thickness as a RATIO of its height, not a pixel count. Held as an
   absolute it stayed 20-50px while the book grew to most of the viewport,
   which made every book a wafer. A real hardback is about a tenth of its
   height, and that proportion is most of what makes the pile read as solid
   objects rather than sheets of card.

   Measured off the reference: even its slimmest volume is a chunky hardback at
   ~0.12 of its height. Nothing here should read as a pamphlet. */
const MIN_RATIO = 0.105;
const MAX_RATIO = 0.165;

export function depthRatioFor(pageCount) {
  if (!pageCount) return MIN_RATIO + 0.02;
  /* 150pp -> thin, 900pp -> thick, clamped either side. */
  const t = Math.min(1, Math.max(0, (pageCount - 150) / 750));
  return MIN_RATIO + t * (MAX_RATIO - MIN_RATIO);
}

export function placeAt(p, w, depth) {
  return {
    y: yAt(p, w, depth),
    /* Clamped at nought going forward. The run is meant to RECEDE as it goes
       down, which is what p > 0 does. Left unclamped, a book above the top of
       the run travels the other way and comes at the camera, so it swells as
       it leaves -- by the head of the run it is 7% oversize and hangs off both
       sides of the canvas. Nothing wanted that; it was just the same term
       carried into a domain it was not written for. */
    z: Math.min(0, p * Z_PER_STEP),
    phi: phiAt(p),
  };
}
